import { useQuery } from '@apollo/client';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { Content, Stack, Text } from '@eluve/components';
import {
  SUMMARY_TAB,
  TRANSCRIPT_TAB,
  toDocumentTabId,
  useAppointmentTabNav,
  useAppointmentTranscript,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentTabBar,
  AppointmentTabContent,
  DocumentTabContent,
  ExternalChartLink,
  PdfDocumentTabContent,
  SignedSummaryResult,
  getDocumentReferencesByAppointmentQuery,
} from '@eluve/frontend-feature-appointment';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { AppointmentName } from '@eluve/smart-blocks';

import {
  EluveAdminControlsPanel,
  EluveAdminControlsProvider,
  EluveAdminControlsToggle,
} from './EluveAdminControls';
import { appointmentLayoutStyles } from './appointmentLayoutStyles';
import { AppointmentSummaryFooter } from './components/AppointmentSummaryFooter';
import { ReadonlyAppointmentDetails } from './components/ReadonlyAppointmentDetails';

interface SignedAppointmentLayoutProps {
  appointmentId: string;
}

export const SignedAppointmentLayout: React.FC<
  SignedAppointmentLayoutProps
> = ({ appointmentId }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { transcript } = useAppointmentTranscript();
  const { tab } = useAppointmentTabNav();
  const isDocGenEnabled = useFeatureFlagEnabled('DOCUMENT_GENERATION') ?? false;

  const { data: docsData } = useQuery(getDocumentReferencesByAppointmentQuery, {
    variables: { tenantId, appointmentId },
    skip: !isDocGenEnabled,
  });
  const documents = isDocGenEnabled ? (docsData?.documentReferences ?? []) : [];

  return (
    <>
      <ReadonlyAppointmentDetails appointmentId={appointmentId} />
      <Content padded>
        <EluveAdminControlsProvider>
          <Stack
            className={appointmentLayoutStyles()}
            fullWidth={false}
            gap={2}
          >
            <AppointmentTabBar documents={documents} />

            <Stack
              className="p-2"
              direction="horizontal"
              align="center"
              justify="between"
            >
              <AppointmentName readonly />
              <EluveAdminControlsToggle />
            </Stack>
            <EluveAdminControlsPanel readonlyMode />
            <ExternalChartLink appointmentId={appointmentId} />
            <AppointmentTabContent tab={TRANSCRIPT_TAB}>
              <div className="h-full overflow-y-auto pb-8">
                <Text
                  weight="regular"
                  color="secondary"
                  className="px-2 align-baseline leading-10! whitespace-pre-line"
                >
                  {transcript}
                </Text>
              </div>
            </AppointmentTabContent>
            <AppointmentTabContent tab={SUMMARY_TAB}>
              <SignedSummaryResult appointmentId={appointmentId} />
            </AppointmentTabContent>
            {isDocGenEnabled &&
              documents.map((doc) => (
                <AppointmentTabContent
                  key={doc.id}
                  tab={toDocumentTabId(doc.id)}
                >
                  {doc.contentType === 'application/pdf' ? (
                    <PdfDocumentTabContent
                      documentId={doc.id}
                      title={doc.title}
                      meta={doc.meta}
                    />
                  ) : (
                    <DocumentTabContent
                      documentId={doc.id}
                      tenantId={tenantId}
                      title={doc.title}
                      content={doc.content}
                      readonly
                    />
                  )}
                </AppointmentTabContent>
              ))}
          </Stack>
        </EluveAdminControlsProvider>
      </Content>
      {tab === 'summary' && <AppointmentSummaryFooter />}
    </>
  );
};
